import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, Minus, Plus, ArrowLeft, CreditCard } from "lucide-react";
import { readConsumerRuntimeDeviceMode, readConsumerRuntimeTableCode } from "../runtime-device-mode";
import { trn } from "../shared/i18n";
import "../assets/styles.css";

// 🔹 Definición de Props para evitar errores en Cloudflare
interface CartProps {
  cart: any[];
  setCart: React.Dispatch<React.SetStateAction<any[]>>;
}

function toNum(n: any): number {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function money(n: number) {
  return `${n.toFixed(2)} €`;
}

// ✅ Componente Cart con iconos mejorados y detalle de opciones/comentarios
const Cart: React.FC<CartProps> = ({ cart, setCart }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const deviceMode = readConsumerRuntimeDeviceMode();
  const tableCode = readConsumerRuntimeTableCode();
  const returnToMenuSearch = typeof (location.state as any)?.returnToMenuSearch === "string"
    ? (location.state as any).returnToMenuSearch
    : "";
  const fallbackMenuPath =
    deviceMode === "table"
      ? `/menu?type=eat-in${tableCode ? `&table=${encodeURIComponent(tableCode)}` : ""}`
      : "/menu?type=takeaway";
  const menuPath = returnToMenuSearch ? `/menu${returnToMenuSearch}` : fallbackMenuPath;

  const cameFromFloatingCart = (location.state as any)?.fromFloatingCart || false;
  const [localCart, setLocalCart] = useState<any[]>(cart);
  const firstLoad = useRef(true);
  void cameFromFloatingCart;
  void localCart;

  useEffect(() => {
    if (cart.length === 0) {
      navigate(menuPath);
      return;
    }

    if (firstLoad.current) {
      firstLoad.current = false; // Evita reset visual
    } else {
      setLocalCart(cart);
    }
  }, [cart, navigate, menuPath]);

  const increaseQuantity = (index: number) => {
    setCart((prevCart) =>
      prevCart.map((item, i) =>
        i === index ? { ...item, quantity: (toNum(item.quantity) || 0) + 1 } : item
      )
    );
  };

  const decreaseQuantity = (index: number) => {
    setCart((prevCart) =>
      prevCart
        .map((item, i) =>
          i === index ? { ...item, quantity: (toNum(item.quantity) || 0) - 1 } : item
        )
        .filter((item) => toNum(item.quantity) > 0)
    );
  };

  // Total
  const totalPrice = cart.reduce(
    (acc, item) => acc + toNum(item.price) * toNum(item.quantity),
    0
  );

  return (
    <div className="consumer-cart-page">
      <div className="consumer-cart-shell">
        <header className="consumer-cart-header">
          <h1 className="consumer-cart-title">
            <ShoppingCart size={42} aria-hidden />
            <span>{trn("app.consumer.cart.title", "Your order")}</span>
          </h1>
        </header>

        <div className="consumer-cart-layout">
          <section className="consumer-cart-items">
            <div className="consumer-cart-list">
              {cart.map((item, index) => {
                const name = item.name ?? trn("app.consumer.common.product", "Product");
                const unit = toNum(item.price);
                const qty = toNum(item.quantity) || 0;
                const lineTotal = unit * qty;
                const imageUrl = item.largeImage || item.image || "";

                const options = Array.isArray(item.options) ? item.options : [];
                const rawComment: string | null = item.note ?? item.comment ?? null;
                const normalizedComment = typeof rawComment === "string" ? rawComment.trim() : "";
                const lowerComment = normalizedComment.toLowerCase();
                const looksLikeLegacyModifierSummary =
                  options.length > 0 &&
                  normalizedComment.length > 0 &&
                  options.some((opt: any) => {
                    const optName = String(opt.optionName || opt.name || opt.label || "")
                      .trim()
                      .toLowerCase();
                    return optName.length > 0 && lowerComment.includes(optName);
                  });
                const comment = looksLikeLegacyModifierSummary ? null : normalizedComment || null;

                return (
                  <article key={`cart-item-${item.id ?? index}-${index}`} className="consumer-cart-item">
                    <div className="consumer-cart-item__main">
                      {imageUrl ? (
                        <div className="consumer-cart-item__image-wrap">
                          <img
                            src={imageUrl}
                            alt={name}
                            className="consumer-cart-item__image"
                            loading="lazy"
                          />
                        </div>
                      ) : null}

                      <div className="consumer-cart-item__top">
                        <strong className="consumer-cart-item__name">{name}</strong>
                      </div>

                      <p className="consumer-cart-item__unit-price">
                        {trn("app.consumer.cart.unit_price", "{price} / unit", { price: money(unit) })}
                      </p>

                      {options.length > 0 && (
                        <div className="consumer-cart-item__modifiers">
                          {options.map((opt: any, i: number) => {
                            const optionQty = toNum(opt.qty) || 1;
                            const optName =
                              opt.optionName ||
                              opt.name ||
                              opt.label ||
                              trn("app.consumer.product_modal.option_fallback", "Option {index}", { index: i + 1 });
                            const up = toNum(opt.unitPrice || opt.price || 0);
                            const showPrice = up > 0 ? `+${money(up)}` : "";
                            return (
                              <div key={`opt-${i}`} className="consumer-cart-item__modifier">
                                <span className="consumer-cart-item__modifier-name">
                                  {optionQty > 1 ? `${optionQty}× ` : ""}
                                  {optName}
                                </span>
                                {showPrice ? (
                                  <span className="consumer-cart-item__modifier-price">{showPrice}</span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {comment && <p className="consumer-cart-item__comment">{comment}</p>}
                    </div>

                    <div className="consumer-cart-item__side">
                      <p className="consumer-cart-item__line-total">{money(lineTotal)}</p>
                      <div className="consumer-cart-controls">
                        <button
                          className="consumer-cart-control"
                          onClick={() => decreaseQuantity(index)}
                          aria-label={trn("app.consumer.cart.remove_unit", "Remove unit")}
                        >
                          <Minus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                        </button>

                        <span className="consumer-cart-quantity">{qty}</span>

                        <button
                          className="consumer-cart-control consumer-cart-control--accent"
                          onClick={() => increaseQuantity(index)}
                          aria-label={trn("app.consumer.cart.add_unit", "Add unit")}
                        >
                          <Plus size={24} strokeWidth={2.75} absoluteStrokeWidth aria-hidden />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <aside className="consumer-cart-footer">
            <div className="consumer-cart-summary-label">
              {trn("app.consumer.cart.title", "Your order")}
            </div>

            <div className="consumer-cart-total">
              {trn("app.consumer.cart.total", "Total: {price}", { price: money(totalPrice) })}
            </div>

            <div className="consumer-cart-actions">
              <button className="consumer-cart-pay" onClick={() => navigate("/payment")}>
                <CreditCard size={30} aria-hidden />
                <span>
                  {deviceMode === "table"
                    ? trn("app.consumer.cart.send_order", "Send order")
                    : trn(
                        "app.consumer.cart.go_to_payment",
                        "Go to payment",
                        undefined,
                        "Primary CTA button that takes the user directly to choose the payment method and finish the order."
                      )}
                </span>
              </button>

              <button
                className="consumer-cart-back"
                onClick={() =>
                  navigate(menuPath, { state: { fromCart: true } })
                }
              >
                <ArrowLeft size={30} aria-hidden />
                <span>{trn("app.consumer.cart.back_to_menu", "Back to menu")}</span>
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Cart;
